export const CORE_FEATURE_FLAGS = [
    {
        key: 'groups.hierarchy',
        defaultValue: false,
        scope: 'global',
        ownerModule: 'identity/groups',
        description: 'Allow nested groups (parent/child group relationships).',
        schemaVersion: 1,
    },
    {
        key: 'groups.tags',
        defaultValue: false,
        scope: 'global',
        ownerModule: 'identity/groups',
        description: 'Tag/category facets in the group directory.',
        schemaVersion: 1,
    },
    {
        key: 'groups.cross-group-forum',
        defaultValue: false,
        scope: 'global',
        ownerModule: 'identity/groups',
        description: 'Allow an Everyone-scoped site-wide forum.',
        schemaVersion: 1,
    },
    {
        key: 'groups.transfer',
        defaultValue: true,
        scope: 'global',
        ownerModule: 'identity/groups',
        description: 'Allow group owners or managers to transfer members and direct family between groups with retained article access.',
        schemaVersion: 1,
    },
    {
        key: 'groups.directory.public',
        defaultValue: true,
        scope: 'global',
        ownerModule: 'identity/groups',
        description: 'Whether `/groups` is browsable by guests.',
        schemaVersion: 1,
    },
    {
        key: 'members.directory.public',
        defaultValue: false,
        scope: 'global',
        ownerModule: 'identity/members',
        description: 'Whether the `/members` directory is visible to guests.',
        schemaVersion: 1,
    },
    {
        key: 'members.parent-bind.code',
        defaultValue: true,
        scope: 'global',
        ownerModule: 'identity/members',
        description: 'Allow parent-binding via 24h codes.',
        schemaVersion: 1,
    },
    {
        key: 'members.parent-bind.manual',
        defaultValue: true,
        scope: 'global',
        ownerModule: 'identity/members',
        description: 'Allow manual parent-binding by managers.',
        schemaVersion: 1,
    },
    {
        key: 'permissions.section-gates',
        defaultValue: true,
        scope: 'global',
        ownerModule: 'identity/permissions',
        description: 'Per-section visibility gates inside articles.',
        schemaVersion: 1,
    },
    {
        key: 'permissions.paywall',
        defaultValue: false,
        scope: 'global',
        ownerModule: 'identity/permissions',
        description: 'Enable paywall ACL mode.',
        schemaVersion: 1,
    },
    {
        key: 'permissions.password-articles',
        defaultValue: true,
        scope: 'global',
        ownerModule: 'identity/permissions',
        description: 'Enable password-protected articles.',
        schemaVersion: 1,
    },
    {
        key: 'permissions.audit-strict',
        defaultValue: true,
        scope: 'global',
        ownerModule: 'identity/permissions',
        description: 'Audit every manager+ override (turn off for perf in tests).',
        schemaVersion: 1,
    },
    {
        key: 'articles.scheduled-publish',
        defaultValue: true,
        scope: 'global',
        ownerModule: 'content/articles',
        description: 'Allow POST /articles/:slug/publish to accept a future `at` and enter scheduled state.',
        schemaVersion: 1,
    },
    {
        key: 'articles.scheduled-unpublish',
        defaultValue: false,
        scope: 'global',
        ownerModule: 'content/articles',
        description: 'Honour `scheduled_unpublish_at` — cron flips published → unpublished at the deadline.',
        schemaVersion: 1,
    },
    {
        key: 'articles.password',
        defaultValue: false,
        scope: 'global',
        ownerModule: 'content/articles',
        description: 'Enable the `access_mode=password` branch (per-article password challenge).',
        schemaVersion: 1,
    },
    {
        key: 'articles.paywall',
        defaultValue: false,
        scope: 'global',
        ownerModule: 'content/articles',
        description: 'Enable the `access_mode=paywall` branch (price + currency unlock).',
        schemaVersion: 1,
    },
    {
        key: 'articles.allowlist',
        defaultValue: false,
        scope: 'global',
        ownerModule: 'content/articles',
        description: 'Enable the `access_mode=allowlist` branch (per-article allowlist of user ids).',
        schemaVersion: 1,
    },
    {
        key: 'articles.versions',
        defaultValue: true,
        scope: 'global',
        ownerModule: 'content/articles',
        description: 'Snapshot every content edit into article_versions + expose list / get / restore routes. Default ON — every PATCH that touches title / summary / body / cover / SEO writes a snapshot, and the writer surface ships its version-history panel pre-wired. Flip OFF to run a slim instance with no version history (the table stays in place; existing rows survive a flip-back).',
        schemaVersion: 1,
    },
    {
        key: 'articles.slug-pinyin-fallback',
        defaultValue: true,
        scope: 'global',
        ownerModule: 'content/articles',
        description: 'When the create-article request omits `slug`, derive one from the title (Chinese → pinyin → ASCII slugify) and resolve uniqueness with -2..-5 suffix retries before falling back to a nanoid suffix. Off → POST /articles requires an explicit slug as before.',
        schemaVersion: 1,
    },
    {
        key: 'articles.auto-translation',
        defaultValue: true,
        scope: 'global',
        ownerModule: 'content/articles',
        description: 'Generate an AI translation snapshot in the background when an article reaches published state. Requires AI_API_KEY; disabled instances simply skip the subscriber work.',
        schemaVersion: 1,
    },
    {
        key: 'articles.collaboration',
        defaultValue: true,
        scope: 'global',
        ownerModule: 'content/articles',
        description: 'Enable article collaboration grants, Yjs WebSocket rooms, presence, and collaboration article list scope.',
        schemaVersion: 1,
    },
    {
        key: 'articles.wechat-export',
        defaultValue: true,
        scope: 'global',
        ownerModule: 'content/articles',
        description: 'Enable WeChat public-account compatible HTML preview, copy, and download surfaces for articles.',
        schemaVersion: 1,
    },
    {
        key: 'articles.wechat-custom-themes',
        defaultValue: true,
        scope: 'global',
        ownerModule: 'content/articles',
        description: 'Allow members to create and edit structured WeChat export themes for article layout.',
        schemaVersion: 1,
    },
    {
        key: 'comments.dom-editor-readonly-preview',
        defaultValue: true,
        scope: 'global',
        ownerModule: 'content/comments',
        description: 'Allow the comment body surface to render validated DOM-Editor comment document payloads through the guarded read-only preview adapter. Off keeps legacy comment block rendering as the only active path.',
        schemaVersion: 1,
    },
    {
        key: 'comments.dom-editor-composer',
        defaultValue: true,
        scope: 'global',
        ownerModule: 'content/comments',
        description: 'Allow new comments, replies, and existing DOM-Editor comment bodies to use the guarded DOM-Editor comment composer path. Off keeps the legacy comment composer as the only creation/edit path.',
        schemaVersion: 1,
    },
    {
        key: 'forum.dom-editor-readonly-preview',
        defaultValue: true,
        scope: 'global',
        ownerModule: 'community/forum',
        description: 'Allow forum thread, post, and revision reader surfaces to render validated DOM-Editor forum document payloads through the guarded read-only adapter. Off keeps legacy forum block rendering as the only active path.',
        schemaVersion: 1,
    },
    {
        key: 'annotations.tracked-changes',
        defaultValue: false,
        scope: 'global',
        ownerModule: 'content/annotations',
        description: 'Allow type=insertion / type=deletion annotations (tracked changes). When off, the API refuses non-comment types with 422.',
        schemaVersion: 1,
    },
    {
        key: 'annotations.scope-private',
        defaultValue: true,
        scope: 'global',
        ownerModule: 'content/annotations',
        description: 'Allow scope=private annotations (author + manager+ only).',
        schemaVersion: 1,
    },
    {
        key: 'annotations.scope-group',
        defaultValue: true,
        scope: 'global',
        ownerModule: 'content/annotations',
        description: 'Allow scope=group annotations (visibility via resource_groups).',
        schemaVersion: 1,
    },
    {
        key: 'annotations.notify-orphan',
        defaultValue: false,
        scope: 'global',
        ownerModule: 'content/annotations',
        description: 'Notify the annotation author when an article body edit drops the anchor (server-side reanchor pass found no match for the captured snippet). Default OFF — most authors do not need a ping for every drift; flip on for compliance / annotation-heavy workflows.',
        schemaVersion: 1,
    },
];
//# sourceMappingURL=registry-core.js.map